import React, { useEffect, useMemo } from 'react';
import {
  TableComponent,
  SortingColumnsType,
} from "../../../../../../components/table-component/table";
import {
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { AdminDashboardService } from "../../../../../../core/services/admin-dashboard.service";
import '../platform.scss';
import { Loading } from "../../../../../../redux/action";
import { connect, useDispatch } from "react-redux";
import { LoadingButton } from "@mui/lab";
import dayjs from "dayjs";
import { Grid } from "@mui/material";
import { AdminPageHead, AdminTabs } from '../../components/AdminPageHead';

const columnDisplayName = {
  name: "Name",
  primary_email: "Email",
  primary_phone: "Phone",
  platform_type: "SM Type",
  username: "SM Name",
  token_disconnected_dt: "Disconnected at",
  remark: "Remark",
  remark_description: "Remark Description",
};

const sortingColumnType: SortingColumnsType[] = [
  {
    name: "name",
    columnSize: "180px",
  },
  {
    name: "primary_email",
    columnSize: "180px",
  },
  {
    name: "primary_phone",
    columnSize: "120px",
  },
  {
    name: "platform_type",
    columnSize: "120px",
  },
  {
    name: "username",
    columnSize: "150px",
  },
  {
    name: "token_disconnected_dt",
    columnSize: "180px",
  },
  {
    name: "remark",
    columnSize: "150px",
  },
  {
    name: "remark_description",
    columnSize: "250px",
  },
];

interface Props {
  loading: (data: any) => any;
  userData: any;
}

const adminDashboardService = new AdminDashboardService();

const DisconnectFeedbacks: React.FC<Props> = (props: Props) => {
  const [renderData, setRenderData] = useState<any>([]);
  const [disconnectData, setDisconnectData] = useState<any>([]);
  const [formData, setFormData] = useState<any>({
    start_date: "",
    end_date: "",
  });
  const [searchName, setSearchName] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const dateFormat = "YYYY-MM-DD";

  const filteredDisconnectData = useMemo(() => {
    const search = searchName.trim().toLowerCase();
    if (!search) return disconnectData;
    return (disconnectData || []).filter((row: any) =>
      [row.name, row.primary_email, row.primary_phone].some(
        (field: any) => field && String(field).toLowerCase().includes(search)
      )
    );
  }, [disconnectData, searchName]);

  const hasActiveFilters = Boolean(searchName || formData.start_date || formData.end_date);

  useEffect(() => {
    getDisconnectFilter(formData);
  }, []);


  async function getDisconnectFilter(formData: any) {
    try {
      props.loading(true);
      setLoading(true);
      const res = await adminDashboardService.getAdminDashDisconnectData(
        formData
      );
      if (res?.data?.status) {
        setDisconnectData(res?.data?.rows);
      }
    } catch (error) {
      console.error("Error fetching getDisconnectFilter:", error);
    } finally {
      props.loading(false);
      setLoading(false);
    }
  }

  async function handleSearch() {
    try {
      if (!searchName.trim() && !formData.start_date && !formData.end_date) {
        setDateError("Please enter a name/email/phone or select a date range to search");
        return;
      }
      if (formData.start_date === "" && formData.end_date != "") {
        setDateError("Please enter start date");
        return;
      } else {
        if (formData.end_date === "" && formData.start_date != "") {
          setDateError("Please enter end date");
          return;
        } else {
          if (formData.start_date > formData.end_date) {
            setDateError("Start date should be less than end date");
            return;
          } else {
            setDateError("");
            setLoading(true);
            getDisconnectFilter(formData);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching handleSearch:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleClear() {
    const resetFormData = {
      start_date: "",
      end_date: "",
    };
    setSearchName("");
    setFormData(resetFormData);
    setDateError("");
    getDisconnectFilter(resetFormData);
  }


  return (
    <div className='adminDashboardPlatformMostOutterDiv disconnect-feedback-main'>
      <AdminPageHead title="Platform" subtitle="Campaigns and creator feedback across the platform." />
      <AdminTabs tabs={[{ to: '/brand/admin-dashboard/campaign-details', label: 'Campaign Details' }, { to: '/brand/admin-dashboard/disconnect-feedbacks', label: 'Disconnect Feedbacks' }]} />
      {dateError && <span className="error">{dateError}</span>}
      <div className="date_range_search">
        <Grid container spacing={2} sx={{alignItems:'flex-end'}}>
          <Grid item sm={3} md={3} xs={12} xl={3}>
            <TextField
              className="w-100"
              label="Name/Email/Phone"
              size="small"
              value={searchName}
              onChange={(e: any) => setSearchName(e.target.value)}
            />
          </Grid>
          <Grid item sm={3} md={3} xs={12} xl={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["StartDate"]}>
                <MobileDatePicker
                  className="w-100"
                  label="Disconnected start date"
                  format="DD-MM-YYYY"
                  maxDate={dayjs(new Date())}
                  value={formData.start_date ? dayjs(formData.start_date) : null}
                  onChange={(e: any) =>
                    setFormData({
                      ...formData,
                      start_date: e?.format(dateFormat),
                    })
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
          <Grid item sm={3} md={3} xs={12} xl={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["EndDate"]}>
                <MobileDatePicker
                  className="w-100"
                  format="DD-MM-YYYY"
                  label="Disconnected end date"
                  maxDate={dayjs(new Date())}
                  value={formData.end_date ? dayjs(formData.end_date) : null}
                  onChange={(e: any) =>
                    setFormData({
                      ...formData,
                      end_date: e?.format(dateFormat),
                    })
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
          <Grid item sm={3} md={3} xs={12} xl={3} className="btn-div">
            <LoadingButton
              className="btn btn-primary btn-sm"
              loading={loading}
              onClick={() => handleSearch()}
            >
              Search
            </LoadingButton>
            {hasActiveFilters && (
              <LoadingButton
                className="btn btn-outline-primary btn-sm ms-2"
                onClick={() => handleClear()}
              >
                Clear
              </LoadingButton>
            )}
          </Grid>
        </Grid>
      </div>

      <div className="adminDashboardPlatformContentDivTable">
        <TableComponent
          pagination={true}
          columnDisplayName={columnDisplayName}
          inputDataSource={filteredDisconnectData}
          orderByColumnName=""
          sortingColumnType={sortingColumnType}
          renderData={renderData}
          setRenderData={(e) => setRenderData(e)}
        >
          {renderData.map((data: any, index: number) => {
            return (
              <TableRow key={index} hover role="checkbox">
                <TableCell>{data.name}</TableCell>
                <TableCell>{data.primary_email}</TableCell>
                <TableCell>{data.primary_phone}</TableCell>
                <TableCell>{data.platform_type}</TableCell>
                <TableCell>{data.username}</TableCell>
                <TableCell>{data.token_disconnected_dt ? dayjs(data.token_disconnected_dt).format('LL') : ''}</TableCell>
                <TableCell>{data.remark}</TableCell>
                <TableCell>{data.remark_description}</TableCell>
              </TableRow>
            );
          })}
        </TableComponent>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.UserReducer.UserData,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    loading: (data: any) => dispatch(Loading(data)),
  };
};

const connectToRedux = connect(
  mapStateToProps,
  mapDispatchToProps
)(DisconnectFeedbacks);
export { connectToRedux as DisconnectFeedbacks };